import React, {useMemo} from "react";
import {Controller, SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";

import StyledButton from "../../../components/styledButton/StyledButton";
import InputField from "../../../components/inputField/InputField";
import Dropdown from "../../../components/dropdown/Dropdown";
import TtileSection from "../../eventPage/titleSection/TtileSection";
import {Loader} from "../../../components/loading/Loader";

import {
  createOrderFormSchema,
  generateDefaultFormValues,
  OrderFormData,
} from "../orderFormHelper";
import {SanitizedEventQuestion} from "../../../services/api/event/eventQuestion/getEventQuestions/getEventQuestions";
import {Event} from "../../../services/api/models/Models";
import {getDropdownOptionFromLabel} from "../../../components/dropdown/DropdownHelper";

import styles from "./OrderFormDesktop.module.scss";

type OrderFormDesktopProps = {
  questions: SanitizedEventQuestion[];
  eventData: Event;
  createOrder: (formData: OrderFormData) => void;
  isCreating: boolean;
};

function OrderFormDesktop({
  questions,
  eventData,
  createOrder,
  isCreating,
}: OrderFormDesktopProps) {
  const {title, venue, city, province, date, image} = eventData;

  const defaultValues = useMemo(
    () => generateDefaultFormValues(questions),
    [questions]
  );

  const {
    handleSubmit,
    control,
    formState: {errors, isSubmitting},
  } = useForm<OrderFormData>({
    defaultValues: generateDefaultFormValues(questions),
    resolver: zodResolver(createOrderFormSchema(questions)),
  });

  const submit: SubmitHandler<OrderFormData> = async (formData) => {
    createOrder(formData);
  };

  if (isCreating) {
    return <Loader type="white" />;
  }

  return (
    <div className={styles.contentContainer}>
      {/* Optionally render event details here */}
      <img className={styles.eventImage} src={image} alt="Event Page" />
      <TtileSection
        title={title}
        venue={venue}
        city={city}
        province={province}
        date={date}
      />
      <form className={styles.form} onSubmit={handleSubmit(submit)}>
        {questions.map((q) => (
          <Controller
            key={q.id}
            name={q.id.toString()}
            control={control}
            render={({field: {onChange, value}}) =>
              q.type === "select" ? (
                <Dropdown
                  classname={styles.input}
                  title={q.question}
                  value={getDropdownOptionFromLabel(q.options!, value)}
                  onChange={(selectedOption) => onChange(selectedOption?.label)}
                  options={q.options!}
                  error={errors[q.id.toString()]?.message as string}
                />
              ) : (
                <InputField
                  classname={styles.input}
                  title={q.question}
                  value={value}
                  setValue={onChange}
                  error={errors[q.id.toString()]?.message as string}
                />
              )
            }
          />
        ))}
        <StyledButton
          baseClassname={styles.button}
          text="Checkout"
          disabled={isSubmitting}
          type={"submit"}
        />
      </form>
    </div>
  );
}

export default OrderFormDesktop;
